/**
 * Screen transitions (audit 4.4): how a screen ENTERS when a sequence moves
 * to it, stored on the destination frame at `customData.screenTransition`.
 * One deterministic compositor consumes two already-rendered screen canvases
 * — export and live playback share it, elements/overrides are never touched.
 */
import { gsap } from "gsap";

export type TransitionType =
  | "cut"
  | "dissolve"
  | "wipe"
  | "push"
  | "iris"
  | "smart"
  | "travel";

export type ScreenTransition = {
  type: TransitionType;
  durationMs: number;
  /** wipe/push: travel of the incoming edge — 0 left, 1 right, 2 up, 3 down */
  direction?: 0 | 1 | 2 | 3;
};

export const TRANSITION_TYPES: readonly TransitionType[] = [
  "cut",
  "dissolve",
  "wipe",
  "push",
  "iris",
  "smart",
  "travel",
];

export const DEFAULT_TRANSITION_MS = 600;
const MIN_TRANSITION_MS = 100;
const MAX_TRANSITION_MS = 5000;

/** Validated transition config for a frame, or null when absent, `cut`, or
 * malformed (malformed degrades to a cut — scenes are editable outside UI). */
export const getScreenTransition = (frame: {
  customData?: Record<string, unknown> | null;
}): ScreenTransition | null => {
  const raw = frame.customData?.screenTransition as
    | Record<string, unknown>
    | undefined;
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const type = raw.type as TransitionType;
  if (!TRANSITION_TYPES.includes(type) || type === "cut") {
    return null;
  }
  if (typeof raw.durationMs !== "number" || !Number.isFinite(raw.durationMs)) {
    return null;
  }
  const direction =
    typeof raw.direction === "number" && [0, 1, 2, 3].includes(raw.direction)
      ? (raw.direction as 0 | 1 | 2 | 3)
      : 0;
  return {
    type,
    durationMs: Math.min(
      MAX_TRANSITION_MS,
      Math.max(MIN_TRANSITION_MS, raw.durationMs),
    ),
    direction,
  };
};

/** customData patch storing (or clearing, with null) a frame's transition. */
export const withScreenTransition = (
  frame: { customData?: Record<string, unknown> | null },
  transition: ScreenTransition | null,
): Record<string, unknown> => {
  const { screenTransition: _old, ...rest } = frame.customData ?? {};
  if (!transition || transition.type === "cut") {
    return rest;
  }
  return { ...rest, screenTransition: transition };
};

// fixed progress ease — punchy but calm; per-transition eases are YAGNI
const PROGRESS_EASE = gsap.parseEase("power2.inOut");

/** Eased progress per encoder frame for a transition of `durationMs` at
 * `fps` — deterministic, ≥1 entries, last lands on 1. */
export const transitionProgressPlan = (
  durationMs: number,
  fps: number,
): number[] => {
  const frameMs = 1000 / Math.max(1, fps);
  const count = Math.max(1, Math.ceil(durationMs / frameMs));
  return Array.from({ length: count }, (_, i) =>
    PROGRESS_EASE(Math.min(1, (i + 1) / count)),
  );
};

/** Composites one transition frame: `from` (outgoing screen) and `to`
 * (incoming screen) are opaque, same-size canvases. */
export const drawTransitionFrame = (
  ctx: CanvasRenderingContext2D,
  from: CanvasImageSource,
  to: CanvasImageSource,
  opts: {
    type: TransitionType;
    direction?: number;
    progress: number;
    width: number;
    height: number;
  },
): void => {
  const { type, width: w, height: h } = opts;
  const p = Math.min(1, Math.max(0, opts.progress));
  const dir = opts.direction ?? 0;
  if (p >= 1 || type === "cut") {
    ctx.drawImage(to, 0, 0);
    return;
  }
  switch (type) {
    // smart is normally handled by prepareSmartTransition's draw closure;
    // a caller without one degrades to a dissolve
    case "smart":
    case "dissolve": {
      ctx.drawImage(from, 0, 0);
      ctx.save();
      ctx.globalAlpha = p;
      ctx.drawImage(to, 0, 0);
      ctx.restore();
      ctx.globalAlpha = 1;
      break;
    }
    case "wipe": {
      ctx.drawImage(from, 0, 0);
      ctx.save();
      ctx.beginPath();
      // the incoming screen is revealed from the edge it travels FROM
      switch (dir) {
        case 1: // from the right edge, moving left
          ctx.rect(w * (1 - p), 0, w * p, h);
          break;
        case 2: // from the top, moving down... revealed band grows downward
          ctx.rect(0, 0, w, h * p);
          break;
        case 3: // from the bottom, moving up
          ctx.rect(0, h * (1 - p), w, h * p);
          break;
        default: // 0: from the left edge
          ctx.rect(0, 0, w * p, h);
      }
      ctx.clip();
      ctx.drawImage(to, 0, 0);
      ctx.restore();
      break;
    }
    case "push": {
      // both screens move together — from slides out, to slides in behind it
      const [vx, vy] =
        dir === 1 ? [1, 0] : dir === 2 ? [0, -1] : dir === 3 ? [0, 1] : [-1, 0];
      // + 0 normalizes -0 (a 0-component direction times a negative sign)
      ctx.drawImage(from, vx * w * p + 0, vy * h * p + 0);
      ctx.drawImage(to, -vx * w * (1 - p) + 0, -vy * h * (1 - p) + 0);
      break;
    }
    case "iris": {
      ctx.drawImage(from, 0, 0);
      ctx.save();
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, p * (Math.hypot(w, h) / 2), 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(to, 0, 0);
      ctx.restore();
      break;
    }
    default:
      ctx.drawImage(to, 0, 0);
  }
};

// ---- travel ("free camera") transition ----------------------------------
// The viewport glides across real canvas content between two screen rects.
// Pure math only — Studio drives the live viewport, record.ts blits a
// corridor render; both consume these helpers.

export type TravelRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Live paths ease with this before calling travelRectAt; export progress
 * arrives already eased via transitionProgressPlan. */
export const travelEase: (p: number) => number = PROGRESS_EASE;

/** Straight rect lerp under a clamped progress.
 * ponytail: flat lerp; Prezi-style zoom-out-then-in arc for far-apart
 * waypoints is the upgrade if this feels mechanical. */
export const travelRectAt = (
  from: TravelRect,
  to: TravelRect,
  progress: number,
): TravelRect => {
  const p = Math.min(1, Math.max(0, progress));
  return {
    x: from.x + (to.x - from.x) * p,
    y: from.y + (to.y - from.y) * p,
    width: from.width + (to.width - from.width) * p,
    height: from.height + (to.height - from.height) * p,
  };
};

/** Bounding union — the corridor the export pre-renders. */
export const rectUnion = (a: TravelRect, b: TravelRect): TravelRect => {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return {
    x,
    y,
    width: Math.max(a.x + a.width, b.x + b.width) - x,
    height: Math.max(a.y + a.height, b.y + b.height) - y,
  };
};

/** Export-side endpoint views: recordScreens draws every screen centered at
 * the same export scale, so travel must be a constant-zoom pan between
 * output-sized views centered on each frame — the p=0/p=1 blits then match
 * the adjacent screen stills exactly (equal-size rects make travelRectAt a
 * pure center lerp). */
export const travelExportViews = (
  fromFrame: TravelRect,
  toFrame: TravelRect,
  width: number,
  height: number,
  scale: number,
): { from: TravelRect; to: TravelRect } => {
  const w = width / scale;
  const h = height / scale;
  const centered = (f: TravelRect): TravelRect => ({
    x: f.x + f.width / 2 - w / 2,
    y: f.y + f.height / 2 - h / 2,
    width: w,
    height: h,
  });
  return { from: centered(fromFrame), to: centered(toFrame) };
};
